import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import { PaginatedWorkshopUpdateRequests } from '../api/types';
import config from '../config';

async function getWorkshopUpdateRequests(props: {
  axios: AxiosInstance;
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  assignee?: number;
  workshop_id?: number;
}): Promise<PaginatedWorkshopUpdateRequests> {
  const { axios, searchString, page, itemsPerPage, status = [], assignee, workshop_id } = props;
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/workshop-update-request`, {
    params: {
      searchString: searchString || '',
      page,
      itemsPerPage,
      status: status.length > 0 ? status?.join() : undefined,
      workshop_id: workshop_id,
      user_id: assignee,
    },
  });
  return data;
}

export default function useWorkshopUpdateRequestsQuery(props: {
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  assignee?: number;
  workshop_id?: number;
}) {
  const axios = useAxios();

  const { searchString, page, itemsPerPage, status = [], assignee, workshop_id } = props;
  const toast = useToast();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey({ searchString, page, itemsPerPage, status, assignee, workshop_id }),
    queryFn: () =>
      getWorkshopUpdateRequests({
        axios,
        searchString,
        page,
        itemsPerPage,
        status,
        assignee,
        workshop_id,
      }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, query.error as any);
    }
  }, [query.error, query.isError, toast]);

  return query;
}

export function getKey({
  searchString,
  page,
  itemsPerPage,
  status,
  assignee,
  workshop_id,
}: {
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  assignee?: number;
  workshop_id?: number;
}) {
  return ['WorkshopUpdateRequests', searchString, page, itemsPerPage, status, assignee, workshop_id] as const satisfies QueryKey;
}
useWorkshopUpdateRequestsQuery.getKey = getKey;
